/** @flow */
import React, { useContext, useEffect, useRef } from 'react'
import { useImmer } from 'use-immer'
import { map } from 'lodash'
import type { Asset, Post, User, Pipe, ID } from 'app/core/types'
import resources from 'app/store/resources/index.js'
import { getResources } from 'app/store/selectors/index.js'
import pipe from 'app/core/utils/pipe.jsx'
import { WebsocketContext } from 'app/core/api/WebsocketProvider.tsx'
import { useDispatch } from 'react-redux'
import Topics from './Topics.jsx'
import Threads from './Threads.jsx'
import Chat from './Chat.tsx'
import { addPostFromWS, updatePostFromWS } from '../../../../store/reducers/reduxPosts.ts'

type Props = {|
  router: Object,
  asset: Asset,
  user: User,
  projectId: ID,
|}

function Discussions(props: Props) {
  const { router, asset, user, projectId } = props
  const dispatch = useDispatch()
  const { Route } = router
  const [collapsabeWidth, _setCollapsableWidth] = useImmer([300, 400])
  const selectedThread = useRef(null)
  const { isReady, ws } = useContext(WebsocketContext)

  const setSelectedThread = (threadId) => {
    selectedThread.current = threadId
  }

  const setCollapsabeWidth = (index: 1 | 0) => (width: number) => {
    _setCollapsableWidth((widths) => {
      widths[index] = width
    })
  }

  useEffect(() => {
    if (isReady) {
      ws.addEventListener('message', (event) => {
        const data = JSON.parse(event.data)
        const { event: eventType, data: post } = data
        if (!['create', 'update'].includes(eventType)) return
        if (eventType === 'create') {
          dispatch(addPostFromWS(post))
          const thread = map(getResources(undefined, 'threads', { id: post.thread }))[0]
          const createdPost = {
            ...post,
            authorInst: { ...post.authorInst, id: post.authorInst.uuid },
            id: post.uuid,
          }
          delete createdPost.uuid
          delete createdPost.authorInst.uuid
          if (thread) {
            resources.threads.update(
              {
                id: post.thread,
                lastPost: createdPost.id,
                lastPostInst: createdPost,
                postCount: thread.postCount + 1,
                unreadPostCount:
                  user.asset === post.authorInst.id || selectedThread.current === post.thread
                    ? 0
                    : thread.unreadPostCount + 1,
              },
              { localOnly: true },
            )
          }
        }
        if (eventType === 'update') {
          dispatch(updatePostFromWS(post))
        }
      })
    }
  }, [isReady])

  return (
    <div
      className="flex row noWrap fullWidth"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      }}
    >
      <Topics router={router} asset={asset} getWidth={setCollapsabeWidth(0)} projectId={projectId} />
      <Route
        name="topic"
        component={Threads}
        componentProps={{
          getWidth: setCollapsabeWidth(1),
          selectedThread: selectedThread.current,
          project: projectId,
        }}
      />
      <Route
        name="thread"
        component={Chat}
        componentProps={{
          router,
          collapsabeWidth: collapsabeWidth.reduce((a, b) => a + b),
          selectedThread: setSelectedThread,
          project: projectId,
        }}
      />
    </div>
  )
}

const pipeInst: Pipe<{ projectId: ID }, typeof Discussions> = pipe()
const DiscussionsComponent: React$ComponentType<any> = pipeInst
  .connect((state, props) => ({ user: state.user }))
  .render(Discussions)
export default DiscussionsComponent
